<template>
    <div>
        <v-container class="wager-page toggle animated" fluid>
            <v-row dense>
                <v-col cols="12" md="4">
                <v-text-field
                    v-model="search"
                    variant="outlined"
                    prepend-inner-icon="mdi-magnify"
                    label="Search"
                    density="comfortable"
                    solo
                    hide-details
                    class="wager-search rounded-lg"
                    color="white"
                    style="font-family: Saira Condensed, sans-serif;"></v-text-field>
                </v-col>
                <v-col class="text-right" cols="12" md="8">
                    <v-btn
                    @click="showAddDialog()"
                    prepend-icon="mdi-plus"
                    class="wager-add-btn mt-2 rounded-lg"
                    color="primary"
                    height="40px"
                    elevation="2" style="font-family: Saira Condensed, sans-serif;">
                        Add Rewards
                    </v-btn>
                </v-col>
                <v-col cols="12">
                    <div class="wager-summary">
                        <div class="summary-tile">
                            <v-icon size="22">mdi-trophy-award</v-icon>
                            <div>
                                <span>Campaigns</span>
                                <strong>{{ filteredData.length }}</strong>
                            </div>
                        </div>
                        <div class="summary-tile">
                            <v-icon size="22">mdi-flag-checkered</v-icon>
                            <div>
                                <span>Milestones</span>
                                <strong>{{ totalMilestones }}</strong>
                            </div>
                        </div>
                        <div class="summary-tile">
                            <v-icon size="22">mdi-cash-multiple</v-icon>
                            <div>
                                <span>Top Reward</span>
                                <strong>{{ formatCurrency(highestReward) }}</strong>
                            </div>
                        </div>
                    </div>
                </v-col>
            <v-col cols="12">
                <!-- Loading State -->
                <div v-if="loading" class="mt-2">
                    <v-progress-linear indeterminate color="primary" height="5" class="mt-1 rounded-lg mb-4"></v-progress-linear>
                    <v-row>
                        <v-col cols="12" md="6" lg="4" v-for="n in 3" :key="n">
                            <v-skeleton-loader type="card" class="rounded-lg"></v-skeleton-loader>
                        </v-col>
                    </v-row>
                </div>

                <!-- Cards Display -->
                <div v-else-if="filteredData.length > 0">
                    <v-row>
                        <v-col cols="12" md="6" lg="4" v-for="item in filteredData" :key="item._id || item.id">
                            <v-card 
                                class="wager-reward-card rounded-xl elevation-card premium-card" 
                                elevation="0" 
                                style="height: 100%; position: relative; overflow: hidden;"
                            >
                                <!-- Decorative Background Elements -->
                                <div class="card-bg-decoration"></div>
                                <div class="card-glow" :class="getGlowClass(item.leaderboardType)"></div>
                                
                                <!-- Card Header with premium gradient background -->
                                <div class="card-header pa-3 pb-2 position-relative" :class="getCardHeaderClass(item.leaderboardType)">
                                    <!-- Header Background Pattern -->
                                    <div class="header-pattern"></div>
                                    
                                    <div class="d-flex justify-space-between align-center position-relative">
                                        <div class="d-flex align-center">
                                            <!-- Premium Logo Container -->
                                            <div class="premium-logo-container mr-3">
                                                <div class="logo-backdrop"></div>
                                                <v-img 
                                                    v-if="item.leaderboardType && item.leaderboardType.toLowerCase() === 'stake'"
                                                    class="stake-logo premium-logo" 
                                                    src="../../assets/stake-logo.png"
                                                ></v-img>
                                                <div v-else class="premium-icon-circle">
                                                    <v-icon color="white" size="22">mdi-gift</v-icon>
                                                    <div class="icon-shine"></div>
                                                </div>
                                            </div>
                                            <div class="header-text">
                                                <div class="card-title text-white font-weight-bold mb-1" style="font-family: Saira Condensed, sans-serif;">
                                                    {{ item.leaderboardType || 'Wager Reward' }}
                                                </div>
                                            </div>
                                        </div>
                                        <!-- Enhanced Status Chip -->
                                        <v-chip 
                                            v-if="item.status" 
                                            :color="getStatusColor(item.status)" 
                                            size="small"
                                            class="premium-status-chip"
                                            variant="flat"
                                        >
                                            <div class="chip-glow"></div>
                                            <v-icon start size="12">{{ getStatusIcon(item.status) }}</v-icon>
                                            {{ getStatusText(item.status) }}
                                        </v-chip>
                                    </div>
                                </div>

                                <!-- Premium Card Body -->
                                <v-card-text class="pa-3 pt-2 position-relative" style="font-family: Saira Condensed, sans-serif;">
                                    <!-- Body Background -->
                                    <div class="body-shimmer"></div>
                                    
                                    <div class="reward-details position-relative">
                                        <!-- Timestamp with premium styling -->
                                        <div class="premium-info-row mb-2">
                                            <div class="premium-info-icon">
                                                <div class="icon-bg clock-bg"></div>
                                                <v-icon size="14" color="white">mdi-clock-outline</v-icon>
                                            </div>
                                            <div class="premium-info-content">
                                                <div class="info-label text-caption text-grey-darken-1 mb-1">Created</div>
                                                <div class="info-value text-body-2 font-weight-medium text-grey-darken-3">
                                                    {{ formatDate(item.createdAt) }}
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Additional details if available -->
                                        <div v-if="item.description" class="premium-info-row mb-2">
                                            <div class="premium-info-icon">
                                                <div class="icon-bg info-bg"></div>
                                                <v-icon size="14" color="white">mdi-information-outline</v-icon>
                                            </div>
                                            <div class="premium-info-content">
                                                <div class="info-label text-caption text-grey-darken-1 mb-1">Description</div>
                                                <div class="info-value text-body-2 text-grey-darken-2">{{ item.description }}</div>
                                            </div>
                                        </div>

                                        <!-- Reward amount with premium styling -->
                                        <div v-if="item.amount" class="premium-info-row premium-highlight mb-2">
                                            <div class="premium-info-icon">
                                                <div class="icon-bg money-bg"></div>
                                                <v-icon size="14" color="white">mdi-currency-usd</v-icon>
                                            </div>
                                            <div class="premium-info-content">
                                                <div class="info-label text-caption text-grey-darken-1 mb-1">Reward Amount</div>
                                                <div class="info-value text-body-1 font-weight-bold reward-amount">
                                                    {{ formatCurrency(item.amount) }}
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Points with premium styling -->
                                        <div v-if="item.points" class="premium-info-row premium-highlight mb-2">
                                            <div class="premium-info-icon">
                                                <div class="icon-bg points-bg"></div>
                                                <v-icon size="14" color="white">mdi-star</v-icon>
                                            </div>
                                            <div class="premium-info-content">
                                                <div class="info-label text-caption text-grey-darken-1 mb-1">Points</div>
                                                <div class="info-value text-body-1 font-weight-bold points-amount">
                                                    {{ item.points }} points
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Milestones Configuration -->
                                        <div v-if="item.milestones && item.milestones.length > 0" class="milestones-section mb-2">
                                            <!-- Milestones List -->
                                            <div class="milestones-list">
                                                <div 
                                                    v-for="(milestone, index) in item.milestones.slice(0, 3)" 
                                                    :key="milestone._id || index"
                                                    class="milestone-item d-flex justify-space-between align-center pa-2 mb-1 rounded"
                                                    :class="getMilestoneClass(index)"
                                                >
                                                    <div class="milestone-wager">
                                                        <div class="text-caption text-grey-darken-1">Wager Required</div>
                                                        <div class="text-body-2 font-weight-bold milestone-amount">
                                                            {{ formatCurrency(milestone.requiredWager) }}
                                                        </div>
                                                    </div>
                                                    <v-icon size="16" color="grey-darken-2" class="mx-2">mdi-arrow-right</v-icon>
                                                    <div class="milestone-reward text-right">
                                                        <div class="text-caption text-grey-darken-1">Reward</div>
                                                        <div class="text-body-2 font-weight-bold reward-amount">
                                                            {{ formatCurrency(milestone.reward) }}
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <!-- Show more indicator if there are more than 3 milestones -->
                                                <div v-if="item.milestones.length > 3" class="text-center mt-2">
                                                    <v-chip 
                                                        size="small" 
                                                        variant="outlined" 
                                                        color="primary"
                                                        class="text-caption"
                                                    >
                                                        +{{ item.milestones.length - 3 }} more milestone{{ item.milestones.length - 3 !== 1 ? 's' : '' }}
                                                    </v-chip>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </v-card-text>

                                <!-- Premium Card Actions -->
                                <div class="premium-divider"></div>
                                <v-card-actions class="px-3 py-2 premium-actions position-relative">
                                    <div class="actions-bg"></div>
                                    
                                    <v-spacer></v-spacer>
                                    
                                    <!-- View Users & Milestones button -->
                                    <v-tooltip text="View User Milestones" location="top">
                                        <template v-slot:activator="{ props }">
                                            <v-btn 
                                                v-bind="props"
                                                @click="showUserMilestonesDialog(item)"
                                                class="premium-action-btn info-btn mr-2"
                                                size="small"
                                                variant="flat"
                                            >
                                                <div class="btn-bg info-bg"></div>
                                                <v-icon size="16" class="mr-1">mdi-account-group</v-icon>
                                                Users
                                            </v-btn>
                                        </template>
                                    </v-tooltip>

                                    <!-- Edit button -->
                                    <v-tooltip text="Edit Wager Rewards" location="top">
                                        <template v-slot:activator="{ props }">
                                            <v-btn 
                                                v-bind="props"
                                                @click="showEditDialog(item)"
                                                class="premium-action-btn warning-btn mr-2"
                                                size="small"
                                                variant="flat"
                                            >
                                                <div class="btn-bg warning-bg"></div>
                                                <v-icon size="16" class="mr-1">mdi-pencil</v-icon>
                                                Edit
                                            </v-btn>
                                        </template>
                                    </v-tooltip>

                                    <!-- Delete button -->
                                    <v-tooltip text="Delete Wager Rewards" location="top">
                                        <template v-slot:activator="{ props }">
                                            <v-btn 
                                                v-bind="props"
                                                @click="showDeleteConfirmDialog(item)"
                                                class="premium-action-btn error-btn"
                                                size="small"
                                                variant="flat"
                                            >
                                                <div class="btn-bg error-bg"></div>
                                                <v-icon size="16" class="mr-1">mdi-delete</v-icon>
                                                Delete
                                            </v-btn>
                                        </template>
                                    </v-tooltip>
                                </v-card-actions>
                            </v-card>
                        </v-col>
                    </v-row>
                </div>

                <!-- Empty State -->
                <v-card v-else class="mt-2 rounded-xl elevation-card" elevation="0" style="border: 1px solid rgba(0,0,0,0.08);">
                    <v-card-text class="text-center py-12" style="font-family: Saira Condensed, sans-serif;">
                        <div class="empty-state-icon mb-6">
                            <v-icon size="72" color="rgba(0,0,0,0.3)">mdi-gift-outline</v-icon>
                        </div>
                        <div class="text-h5 text-grey-darken-2 mb-3 font-weight-bold">No Wager Rewards Found</div>
                        <div class="text-body-1 text-grey mb-6" style="max-width: 400px; margin: 0 auto;">
                            {{ search ? 'No rewards match your search criteria. Try adjusting your search terms.' : 'Get started by creating your first wager reward campaign.' }}
                        </div>
                        <v-btn 
                            v-if="!search" 
                            @click="showAddDialog()"
                            color="primary" 
                            size="large"
                            class="mt-2 rounded-lg" 
                            prepend-icon="mdi-plus"
                            variant="elevated"
                            style="font-family: Saira Condensed, sans-serif;"
                        >
                            Create Wager Reward
                        </v-btn>
                        <v-btn 
                            v-else
                            @click="search = null"
                            color="grey" 
                            size="large"
                            class="mt-2 rounded-lg" 
                            prepend-icon="mdi-refresh"
                            variant="text"
                            style="font-family: Saira Condensed, sans-serif;"
                        >
                            Clear Search
                        </v-btn>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        </v-container>

        <v-snackbar
            v-model="snackbar"
            :timeout="2500"
            color="secondary"
            location="top">
            <v-icon>mdi-alert-circle</v-icon>
            <span class="ml-2" style="font-family: Saira Condensed, sans-serif;">{{ snackText }}</span>
        </v-snackbar>

        <WagerRewardsDialog ref="WagerRewardsDialog" @refresh="init()"/>
        <WagerUserMilestonesDialog ref="WagerUserMilestonesDialog" @refresh="init()"/>
    </div>
</template>

<script>
import axios from 'axios';
import { defineComponent } from 'vue';
import WagerRewardsDialog from '../Dialogs/Views/WagerRewardsDialog.vue';
import WagerUserMilestonesDialog from '../Dialogs/Views/WagerUserMilestonesDialog.vue';

export default defineComponent({
    components: {
        WagerRewardsDialog,
        WagerUserMilestonesDialog
    },
    data(){
        return {
            data: [],

            refundDialog: false,
            itemToRefund: null,
            loadingRefund: false,
            search: null,
            loading: false,
            
            snackbar: false,
            snackText: null,
        }
    },
    computed: {
        filteredData() {
            if (!this.search) {
                return this.data;
            }
            
            const searchTerm = this.search.toLowerCase();
            return this.data.filter(item => {
                const type = (item.leaderboardType || '').toLowerCase();
                const date = this.formatDate(item.createdAt).toLowerCase();
                const status = (item.status || '').toLowerCase();
                
                return type.includes(searchTerm) || 
                       date.includes(searchTerm) || 
                       status.includes(searchTerm);
            });
        },
        totalMilestones() {
            return this.filteredData.reduce((total, item) => {
                return total + (Array.isArray(item.milestones) ? item.milestones.length : 0);
            }, 0);
        },
        highestReward() {
            return this.filteredData.reduce((highest, item) => {
                if (!Array.isArray(item.milestones)) return highest;

                const itemHighest = item.milestones.reduce((currentHighest, milestone) => {
                    return Math.max(currentHighest, Number(milestone.reward || 0));
                }, 0);

                return Math.max(highest, itemHighest);
            }, 0);
        }
    },
    methods: {
        async init(){
            const token = localStorage.getItem('seham-admin-accesstoken')
            if(token){
                this.loading = true
                axios.get(process.env.VUE_APP_SERVER_URL + '/api/admin/wager-rewards',
                { headers: 
                    { 
                        'Authorization': `Bearer ${token}`,
                        'x-admin-key': process.env.VUE_APP_X_ADMIN_KEY,
                    }
                }
                ).then((res)=>{
                    if(res){
                        this.data = res.data.wagerRewards;
                        console.log(this.data)
                        this.loading = false
                    }
                }).catch((error) => {
                    if (error.response) {
                        if (error.response.status === 401) {
                                localStorage.removeItem("vuex");
                                localStorage.removeItem("seham-admin-accesstoken");
                                window.location.reload();
                        } else {
                            console.log(`Error: ${error.response.status}`);
                        }
                    } 
                    else if (error.request) {
                        console.log('No response received', error.request);
                    } else {
                        console.log('Error', error.message);
                    }
                });
            }
            else{
                localStorage.removeItem("vuex");
                localStorage.removeItem("seham-admin-accesstoken");
                window.location.reload();
            }
        },
        showAddDialog(){
            this.$refs.WagerRewardsDialog.initAdd();
        },
        showEditDialog(data){
            this.$refs.WagerRewardsDialog.initUpdate(data);
        },
        showUserMilestonesDialog(data){
            this.$refs.WagerUserMilestonesDialog.initView(data);
        },
        async showDeleteConfirmDialog(data){
            const confirmed = await this.showConfirmDialog(
                'Delete Wager Rewards',
                `Are you sure you want to delete the wager rewards for "${data.leaderboardType}"? This action cannot be undone and will affect all user milestones.`,
                'Delete',
                'error'
            );
            
            if (confirmed) {
                await this.deleteWagerRewards(data._id);
            }
        },
        async deleteWagerRewards(id) {
            const token = localStorage.getItem('seham-admin-accesstoken');
            
            try {
                this.loading = true;
                const response = await axios.delete(
                    `${process.env.VUE_APP_SERVER_URL}/api/admin/wager-rewards/${id}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'x-admin-key': process.env.VUE_APP_X_ADMIN_KEY,
                        }
                    }
                );
                
                if (response.data) {
                    this.showSnackbar('Wager rewards deleted successfully', 'success');
                    this.init(); // Refresh the data
                }
            } catch (error) {
                if (error.response?.status === 401) {
                    localStorage.removeItem("vuex");
                    localStorage.removeItem("seham-admin-accesstoken");
                    window.location.reload();
                } else {
                    this.showSnackbar(
                        error.response?.data?.message || 'Failed to delete wager rewards', 
                        'error'
                    );
                }
                console.error('Delete error:', error);
            } finally {
                this.loading = false;
            }
        },
        async showConfirmDialog(title, message, confirmText = 'Confirm', color = 'primary') {
            // Simple confirm dialog using browser confirm for now
            // In a production app, you'd want to use a proper Vuetify dialog
            return window.confirm(`${title}\n\n${message}`);
        },
        showSnackbar(message, color = 'success') {
            this.snackText = message;
            this.snackbar = true;
        },
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            
            const date = new Date(dateString);
            const options = {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            };
            
            return date.toLocaleDateString('en-US', options);
        },
        formatCurrency(amount) {
            if (!amount) return '$0.00';
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
            }).format(amount);
        },
        getStatusColor(status) {
            const statusColors = {
                'pending': 'orange',
                'inprogress': 'blue',
                'completed': 'green',
                'refunded': 'grey',
                'declined': 'red'
            };
            return statusColors[status] || 'grey';
        },
        getStatusText(status) {
            const statusTexts = {
                'pending': 'Pending',
                'inprogress': 'In Progress',
                'completed': 'Completed',
                'refunded': 'Refunded',
                'declined': 'Declined'
            };
            return statusTexts[status] || status;
        },
        getStatusIcon(status) {
            const statusIcons = {
                'pending': 'mdi-clock-outline',
                'inprogress': 'mdi-progress-clock',
                'completed': 'mdi-check-circle',
                'refunded': 'mdi-undo',
                'declined': 'mdi-close-circle'
            };
            return statusIcons[status] || 'mdi-help-circle';
        },
        getCardHeaderClass(leaderboardType) {
            if (leaderboardType && leaderboardType.toLowerCase() === 'stake') {
                return 'stake-header';
            }
            return 'default-header';
        },
        getGlowClass(leaderboardType) {
            if (leaderboardType && leaderboardType.toLowerCase() === 'stake') {
                return 'stake-glow';
            }
            return 'default-glow';
        },
        showRefundDialog(item) {
            // This method should be implemented based on your refund logic
            console.log('Refund dialog for:', item);
            // You can add the refund dialog logic here
        },
        getMilestoneClass(index) {
            // Return different classes for visual variety in milestones
            const classes = [
                'milestone-bronze',
                'milestone-silver', 
                'milestone-gold'
            ];
            return classes[index % classes.length];
        },
    },
    mounted(){
        this.init()
    }
}) 
</script>

<style scoped>
/* ===== PREMIUM CARD BASE ===== */
.premium-card {
    background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 50%, #ffffff 100%);
    position: relative;
}

.elevation-card {
    box-shadow: 
        0 2px 8px rgba(0,0,0,0.06), 
        0 1px 3px rgba(0,0,0,0.04) !important;
}

/* ===== CARD DECORATIVE ELEMENTS ===== */
.card-bg-decoration {
    position: absolute;
    top: 0;
    right: 0;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(83, 96, 145, 0.03) 0%, transparent 70%);
    pointer-events: none;
    z-index: 1;
}

.card-glow {
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border-radius: 16px;
    opacity: 0;
    pointer-events: none;
    z-index: 0;
}

.stake-glow {
    background: linear-gradient(45deg, 
        rgba(26, 26, 26, 0.3) 0%, 
        rgba(64, 64, 64, 0.2) 50%, 
        rgba(26, 26, 26, 0.3) 100%);
}

.default-glow {
    background: linear-gradient(45deg, 
        rgba(83, 96, 145, 0.3) 0%, 
        rgba(139, 146, 214, 0.2) 50%, 
        rgba(83, 96, 145, 0.3) 100%);
}

/* ===== PREMIUM HEADER STYLES ===== */
.card-header {
    position: relative;
    overflow: hidden;
    z-index: 2;
}

.header-pattern {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
        radial-gradient(circle at 20% 30%, rgba(255,255,255,0.15) 1px, transparent 1px),
        radial-gradient(circle at 80% 70%, rgba(255,255,255,0.1) 1px, transparent 1px);
    background-size: 30px 30px, 40px 40px;
    opacity: 0.7;
}

.stake-header {
    background: linear-gradient(135deg, 
        #000000 0%, 
        #1a1a1a 25%, 
        #2d2d2d 50%, 
        #1a1a1a 75%, 
        #000000 100%);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.1);
}

.default-header {
    background: linear-gradient(135deg, 
        #4a5568 0%, 
        #536091 25%, 
        #6b7bc7 50%, 
        #536091 75%, 
        #4a5568 100%);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.2);
}

/* ===== PREMIUM LOGO STYLING ===== */
.premium-logo-container {
    position: relative;
    z-index: 3;
}

.logo-backdrop {
    position: absolute;
    top: -6px;
    left: -6px;
    right: -6px;
    bottom: -6px;
    background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%);
    border-radius: 50%;
}

.premium-logo {
    width: 28px;
    height: 28px;
    object-fit: contain;
    border-radius: 6px;
    background: white;
    padding: 4px;
    box-shadow: 
        0 2px 6px rgba(0,0,0,0.2),
        0 1px 2px rgba(0,0,0,0.1),
        inset 0 1px 0 rgba(255,255,255,0.8);
}

.premium-icon-circle {
    position: relative;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(15px);
    box-shadow: 
        0 2px 6px rgba(0,0,0,0.15),
        inset 0 1px 0 rgba(255,255,255,0.3);
}

.icon-shine {
    display: none;
}

/* ===== HEADER TEXT STYLING ===== */
.card-title {
    font-size: 1.25rem;
    text-shadow: 0 1px 2px rgba(0,0,0,0.3);
    text-transform: capitalize;
    letter-spacing: 0.5px;
}

.card-subtitle {
    font-size: 0.8rem;
    opacity: 0.9;
    text-shadow: 0 1px 1px rgba(0,0,0,0.2);
    font-weight: 500;
}

/* ===== PREMIUM STATUS CHIP ===== */
.premium-status-chip {
    position: relative;
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    overflow: hidden;
    font-weight: 600;
    text-shadow: 0 1px 1px rgba(0,0,0,0.2);
}

.chip-glow {
    display: none;
}

/* ===== PREMIUM BODY STYLING ===== */
.body-shimmer {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(145deg, rgba(255,255,255,0.8) 0%, rgba(248,249,250,0.4) 100%);
    pointer-events: none;
}

/* ===== PREMIUM INFO ROWS ===== */
.premium-info-row {
    display: flex;
    align-items: flex-start;
    padding: 8px 10px;
    border-radius: 8px;
    background: rgba(255,255,255,0.6);
    backdrop-filter: blur(10px);
    position: relative;
}

.premium-highlight {
    background: linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(248,250,255,0.7) 100%);
    border: 1px solid rgba(83, 96, 145, 0.1);
}

.premium-info-icon {
    position: relative;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    flex-shrink: 0;
}

.icon-bg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 6px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.15);
}

.clock-bg { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.info-bg { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); }
.money-bg { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); }
.points-bg { background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%); }

.premium-info-content {
    flex: 1;
}

.info-label {
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.info-value {
    line-height: 1.3;
}

.reward-amount {
    background: linear-gradient(45deg, #4CAF50, #45a049);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.points-amount {
    background: linear-gradient(45deg, #FF9800, #F57C00);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

/* ===== PREMIUM DIVIDER ===== */
.premium-divider {
    height: 1px;
    margin: 0 12px;
    background: linear-gradient(90deg, 
        transparent 0%, 
        rgba(0,0,0,0.1) 20%, 
        rgba(0,0,0,0.15) 50%, 
        rgba(0,0,0,0.1) 80%, 
        transparent 100%);
}

/* ===== PREMIUM ACTIONS ===== */
.premium-actions {
    background: linear-gradient(145deg, rgba(248,250,252,0.8) 0%, rgba(255,255,255,0.6) 100%);
    backdrop-filter: blur(10px);
}

.actions-bg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(248,249,250,0.05) 100%);
}

.premium-action-btn {
    position: relative;
    padding: 6px 12px;
    border-radius: 8px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0.2px;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(0,0,0,0.1);
}

.btn-bg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 10px;
    z-index: -1;
}

.refund-bg {
    background: linear-gradient(135deg, #ff9a56 0%, #ffad56 100%);
}

.primary-bg {
    background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
}

.refund-btn { color: white !important; }
.primary-btn { color: black !important; }

/* ===== PLACEHOLDER STYLING ===== */
.placeholder-content {
    padding: 20px;
    border-radius: 12px;
    background: linear-gradient(145deg, rgba(248,250,252,0.8) 0%, rgba(255,255,255,0.4) 100%);
    border: 2px dashed rgba(0,0,0,0.1);
}

/* ===== ANIMATIONS ===== */
@keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.7; }
    50% { transform: scale(1.1); opacity: 1; }
}

@keyframes shine {
    0% { transform: translateX(-100%) translateY(-100%) rotate(30deg); }
    100% { transform: translateX(100%) translateY(100%) rotate(30deg); }
}

@keyframes shimmer {
    0%, 100% { transform: translateX(-100%); }
    50% { transform: translateX(100%); }
}

/* ===== EMPTY STATE ===== */
.empty-state-icon {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.empty-state-icon::before {
    content: '';
    position: absolute;
    width: 120px;
    height: 120px;
    background: radial-gradient(circle, rgba(83, 96, 145, 0.1) 0%, transparent 70%);
    border-radius: 50%;
    z-index: -1;
}

/* ===== RESPONSIVE DESIGN ===== */
@media (max-width: 768px) {
    .premium-card {
        margin-bottom: 16px;
    }
    
    .card-header {
        padding: 16px !important;
    }
    
    .premium-info-row {
        padding: 8px 10px;
    }
    
    .premium-info-icon {
        width: 28px;
        height: 28px;
        margin-right: 10px;
    }
    
    .premium-action-btn {
        padding: 4px 8px;
        font-size: 0.8rem;
    }
}

@media (max-width: 480px) {
    .premium-info-row {
        padding: 8px 10px;
    }
    
    .premium-info-icon {
        width: 28px;
        height: 28px;
        margin-right: 10px;
    }
}

/* ===== DARK THEME COMPATIBILITY ===== */
.v-theme--dark .premium-card {
    background: linear-gradient(145deg, #1e1e1e 0%, #2d2d2d 50%, #1e1e1e 100%);
    border-color: rgba(255, 255, 255, 0.1);
}

.v-theme--dark .premium-info-row {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
}

.v-theme--dark .empty-state-icon::before {
    background: radial-gradient(circle, rgba(139, 146, 214, 0.2) 0%, transparent 70%);
}

.v-theme--dark .body-shimmer {
    background: linear-gradient(145deg, rgba(255,255,255,0.02) 0%, rgba(248,249,250,0.01) 100%);
}

.v-theme--dark .premium-actions {
    background: linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%);
}

/* New button styles for wager rewards actions */
.info-btn .info-bg {
    background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);
    opacity: 0.9;
}

.warning-btn .warning-bg {
    background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%);
    opacity: 0.9;
}

.error-btn .error-bg {
    background: linear-gradient(135deg, #F44336 0%, #D32F2F 100%);
    opacity: 0.9;
}

.info-btn:hover .info-bg,
.warning-btn:hover .warning-bg,
.error-btn:hover .error-bg {
    opacity: 1;
}

.info-btn,
.warning-btn,
.error-btn {
    color: black !important;
}

/* ===== MILESTONE STYLES ===== */
.milestones-section {
    position: relative;
    z-index: 2;
}

.milestone-bg {
    background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
}

.milestones-list {
    background: linear-gradient(145deg, rgba(139, 92, 246, 0.02) 0%, rgba(124, 58, 237, 0.01) 100%);
    border-radius: 8px;
    padding: 8px;
    border: 1px solid rgba(139, 92, 246, 0.1);
}

.milestone-item {
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.8) 0%, rgba(248, 249, 250, 0.9) 100%);
    border: 1px solid rgba(139, 92, 246, 0.15);
    transition: all 0.2s ease;
    position: relative;
    overflow: hidden;
}

.milestone-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 0%, rgba(255, 255, 255, 0.2) 50%, transparent 100%);
    transform: translateX(-100%);
    transition: transform 0.6s ease;
}

.milestone-item:hover::before {
    transform: translateX(100%);
}

.milestone-item:hover {
    border-color: rgba(139, 92, 246, 0.3);
    box-shadow: 0 2px 8px rgba(139, 92, 246, 0.15);
    transform: translateY(-1px);
}

.milestone-bronze {
    border-left: 3px solid #CD7F32;
}

.milestone-silver {
    border-left: 3px solid #C0C0C0;
}

.milestone-gold {
    border-left: 3px solid #FFD700;
}

.milestone-wager,
.milestone-reward {
    position: relative;
    z-index: 2;
}

.milestone-amount,
.reward-amount {
    background: linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 700 !important;
}

/* Dark theme for milestones */
.v-theme--dark .milestones-list {
    background: linear-gradient(145deg, rgba(139, 92, 246, 0.05) 0%, rgba(124, 58, 237, 0.03) 100%);
    border-color: rgba(139, 92, 246, 0.2);
}

.v-theme--dark .milestone-item {
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(248, 249, 250, 0.03) 100%);
    border-color: rgba(139, 92, 246, 0.2);
}

.v-theme--dark .milestone-item:hover {
    border-color: rgba(139, 92, 246, 0.4);
    box-shadow: 0 2px 8px rgba(139, 92, 246, 0.25);
}

/* Responsive milestone layout */
@media (max-width: 768px) {
    .milestone-item {
        flex-direction: column;
        text-align: center;
        gap: 8px;
    }
    
    .milestone-wager,
    .milestone-reward {
        text-align: center !important;
    }
    
    .milestone-item .mx-2 {
        transform: rotate(90deg);
        margin: 8px 0 !important;
    }
}

/* ===== SEHAM BLACK / GOLD WAGER REWARDS REDESIGN ===== */
.wager-page {
    font-family: Saira Condensed, sans-serif;
    color: var(--seham-text);
}

.wager-search :deep(.v-field) {
    min-height: 48px;
    background: linear-gradient(90deg, rgba(246, 216, 74, 0.08) 0%, rgba(8, 8, 6, 0.96) 100%) !important;
    border: 1px solid rgba(246, 216, 74, 0.28);
    box-shadow: 0 0 24px rgba(246, 216, 74, 0.06);
}

.wager-add-btn {
    min-width: 144px;
    letter-spacing: 0.5px;
    background: #f7cd45 !important;
    color: #000000 !important;
    box-shadow: 0 12px 28px rgba(247, 205, 69, 0.16) !important;
}

.wager-summary {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin: 8px 0 14px;
}

.summary-tile {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 74px;
    padding: 14px 16px;
    border: 1px solid rgba(246, 216, 74, 0.24);
    border-radius: 8px;
    background: linear-gradient(90deg, rgba(246, 216, 74, 0.14) 0%, rgba(19, 17, 10, 0.96) 34%, rgba(4, 4, 3, 0.98) 100%);
    box-shadow: 0 18px 48px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(246, 216, 74, 0.12);
}

.summary-tile .v-icon {
    color: var(--seham-yellow);
    filter: drop-shadow(0 0 10px rgba(246, 216, 74, 0.35));
}

.summary-tile span {
    display: block;
    color: var(--seham-muted);
    font-size: 0.9rem;
}

.summary-tile strong {
    display: block;
    color: var(--seham-yellow-soft);
    font-size: 1.6rem;
    line-height: 1;
}

.premium-card {
    min-height: 438px;
    border: 1px solid rgba(246, 216, 74, 0.28) !important;
    border-radius: 8px !important;
    background: linear-gradient(90deg, rgba(246, 216, 74, 0.12) 0%, rgba(20, 18, 11, 0.98) 30%, rgba(4, 4, 3, 0.98) 100%) !important;
    box-shadow: 0 24px 70px rgba(0, 0, 0, 0.42), 0 0 34px rgba(246, 216, 74, 0.08) !important;
}

.premium-card:hover {
    transform: translateY(-3px);
    border-color: rgba(246, 216, 74, 0.5) !important;
    box-shadow: 0 28px 80px rgba(0, 0, 0, 0.5), 0 0 38px rgba(246, 216, 74, 0.14) !important;
}

.card-bg-decoration {
    width: 260px;
    height: 260px;
    top: -96px;
    left: -88px;
    right: auto;
    background: radial-gradient(circle, rgba(246, 216, 74, 0.22) 0%, rgba(181, 150, 55, 0.08) 38%, transparent 70%);
}

.card-glow,
.stake-glow,
.default-glow {
    background: none;
}

.card-header,
.stake-header,
.default-header {
    min-height: 64px;
    background: linear-gradient(90deg, rgba(246, 216, 74, 0.2) 0%, rgba(34, 30, 16, 0.98) 34%, rgba(5, 5, 4, 0.98) 100%) !important;
    border-bottom: 1px solid rgba(246, 216, 74, 0.22);
    box-shadow: none;
}

.header-pattern {
    opacity: 0.35;
    background-image:
        radial-gradient(circle at 18% 30%, rgba(246, 216, 74, 0.24) 1px, transparent 1px),
        radial-gradient(circle at 72% 62%, rgba(246, 216, 74, 0.16) 1px, transparent 1px);
}

.premium-logo,
.premium-icon-circle {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: linear-gradient(90deg, #f6d84a 0%, #b59637 52%, #050504 100%) !important;
    border: 1px solid rgba(255, 241, 168, 0.35);
    box-shadow: 0 0 22px rgba(246, 216, 74, 0.22);
}

.premium-logo {
    padding: 5px;
}

.logo-backdrop {
    background: radial-gradient(circle, rgba(246, 216, 74, 0.2) 0%, transparent 70%);
}

.card-title {
    color: #fff7cf !important;
    font-family: Russo One, sans-serif !important;
    letter-spacing: 0;
    text-shadow: 0 0 18px rgba(246, 216, 74, 0.16);
}

.premium-status-chip {
    color: #080806 !important;
    background: linear-gradient(90deg, #f6d84a 0%, #b59637 100%) !important;
    border: 1px solid rgba(255, 241, 168, 0.42);
    text-shadow: none;
}

.body-shimmer {
    background: linear-gradient(90deg, rgba(246, 216, 74, 0.06) 0%, transparent 60%);
}

.premium-info-row,
.premium-highlight {
    background: linear-gradient(90deg, rgba(246, 216, 74, 0.12) 0%, rgba(14, 13, 9, 0.92) 42%, rgba(5, 5, 4, 0.95) 100%) !important;
    border: 1px solid rgba(246, 216, 74, 0.18) !important;
}

.info-label,
.milestone-item .text-caption {
    color: rgba(248, 246, 233, 0.62) !important;
}

.info-value {
    color: #fff7cf !important;
}

.clock-bg,
.info-bg,
.money-bg,
.points-bg,
.milestone-bg {
    background: linear-gradient(90deg, #f6d84a 0%, #b59637 52%, #050504 100%) !important;
}

.reward-amount,
.points-amount,
.milestone-amount {
    background: none !important;
    -webkit-text-fill-color: unset;
    color: var(--seham-yellow-soft) !important;
}

.milestones-list {
    background: rgba(4, 4, 3, 0.45) !important;
    border: 1px solid rgba(246, 216, 74, 0.2) !important;
}

.milestone-item {
    background: linear-gradient(90deg, rgba(246, 216, 74, 0.15) 0%, rgba(15, 14, 9, 0.94) 34%, rgba(5, 5, 4, 0.96) 100%) !important;
    border: 1px solid rgba(246, 216, 74, 0.18) !important;
    border-left: 3px solid #f6d84a !important;
}

.milestone-item:hover {
    border-color: rgba(246, 216, 74, 0.42) !important;
    box-shadow: 0 8px 22px rgba(246, 216, 74, 0.08) !important;
}

.milestone-item .v-icon {
    color: var(--seham-yellow) !important;
}

.premium-divider {
    background: linear-gradient(90deg, rgba(246, 216, 74, 0.42) 0%, rgba(181, 150, 55, 0.18) 42%, transparent 100%);
}

.premium-actions {
    background: linear-gradient(90deg, rgba(246, 216, 74, 0.11) 0%, rgba(13, 12, 8, 0.94) 42%, rgba(4, 4, 3, 0.98) 100%) !important;
    min-height: 54px;
}

.actions-bg {
    background: none;
}

.premium-action-btn {
    color: #080806 !important;
    background: #f7cd45 !important;
    box-shadow: none;
}

.info-btn .info-bg,
.warning-btn .warning-bg,
.error-btn .error-bg {
    background: #f7cd45 !important;
    opacity: 1;
}

.premium-action-btn:hover {
    transform: translateY(-1px);
}

.empty-state-icon::before {
    background: radial-gradient(circle, rgba(246, 216, 74, 0.16) 0%, transparent 70%);
}

@media (max-width: 960px) {
    .wager-summary {
        grid-template-columns: 1fr;
    }
}
</style>
